import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, ChevronLeft, ChevronRight, Download, ImagePlus, Loader2, LocateFixed, X } from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import AddressAutocomplete from "../components/AddressAutocomplete";
import CountryPicker from "../components/CountryPicker";
import HostDeclaration from "../components/HostDeclaration";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PageLoading } from "../components/States";
import { CANCELLATION_POLICIES, policyLabel } from "../lib/cancellation";
import { fmtMoney, setPageTitle, tzLabel } from "../lib/format";

const SPACE_TYPES = ["Underground", "Covered", "Open", "Rooftop", "Private"];
const AMENITY_SUGGESTIONS = ["EV Charging", "CCTV", "Gated", "24/7 Access", "Lighting", "Covered", "Security Guard", "Handicap Accessible"];
const DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

export const emptyListing = {
  title: "", description: "", address: "", latitude: null, longitude: null,
  price_per_hour: "", price_6h: "", price_12h: "", price_24h: "",
  monthly_enabled: false, monthly_price: "",
  space_type: "Covered", amenities: [], booking_policy: "open", max_duration_hours: "",
  cancellation_policy: "",
  max_vehicle_height_m: "", vehicle_restrictions: "",
  access_instructions: "", images: "", available_days: [], available_start_hour: "", available_end_hour: "",
  location_confirmed: false, authorization_type: "", attestation_accepted: false,
  currency: "cad", timezone: "",  // property timezone is auto-detected from the pinned coordinates — never the host's device
  country: "CA", region: "",  // country defaults to the primary market (Canada) — clearly changeable; edits prefill the saved value
  postal_code: "",  // geocoder postcode — passed through to Stripe Tax jurisdiction
  flat_rate_enabled: false, flat_rate_duration_choice: "12", flat_rate_custom_hours: "", flat_rate_price: "",
};

// Draft persistence — browser-local (localStorage), scoped per authenticated
// user, NEW listings only (the edit flow never reads or writes drafts). One
// slot per user, so refreshes/revisits overwrite the same draft rather than
// accumulating duplicates. Access instructions (gate codes) and auth tokens
// are NEVER stored locally. Same-browser only — no cross-device sync.
const listingDraftKey = (userId) => `mrp_listing_draft:${userId}`;
const loadListingDraft = (key) => {
  try {
    const d = JSON.parse(localStorage.getItem(key) || "null");
    return d && typeof d === "object" && d.f ? d : null;
  } catch { return null; }
};
// A draft only counts when the host actually entered something — prevents
// empty form visits from writing hollow drafts / triggering restore toasts.
const draftHasContent = (f, photos, urlText) =>
  !!(f && (f.title?.trim() || f.description?.trim() || f.address?.trim() || String(f.price_per_hour || "").trim()))
  || (photos || []).length > 0 || !!(urlText || "").trim();
const clearListingDraft = (key) => { try { localStorage.removeItem(key); } catch { /* storage unavailable */ } };

// Availability windows are interpreted in the listing's timezone (DST-correct).
const TZ_OPTIONS = ["America/Toronto", "America/Vancouver", "America/Edmonton", "America/Winnipeg",
  "America/Halifax", "America/St_Johns", "America/New_York", "America/Chicago", "America/Denver",
  "America/Los_Angeles", "Europe/London", "Europe/Paris", "Australia/Sydney", "UTC"];

export function ListingForm({ initial, onSubmit, submitLabel, testPrefix, requirePolicy = false, payoutGate = null, draftKey = null }) {
  const [step, setStep] = useState(0);
  const [f, setF] = useState(initial);
  const [addrQuery, setAddrQuery] = useState(initial.address || "");
  const [busy, setBusy] = useState(false);
  const [locating, setLocating] = useState(false);
  // Uploaded photos are platform-hosted refs (/api/media/...); the textarea below
  // carries only pasted https URLs. Both merge into `images` on submit.
  const [photos, setPhotos] = useState(() => (initial.images || "").split("\n").map((u) => u.trim()).filter((u) => u.startsWith("/api/media/")));
  const [urlText, setUrlText] = useState(() => (initial.images || "").split("\n").map((u) => u.trim()).filter((u) => u && !u.startsWith("/api/media/")).join("\n"));
  const [uploading, setUploading] = useState(0);

  // ---- Draft autosave (active only when draftKey is provided) ----
  const [draftStatus, setDraftStatus] = useState(null); // null | saving | saved | error
  const draftHydrated = useRef(!draftKey);
  const draftRestored = useRef(false);
  const latestDraft = useRef(null);
  latestDraft.current = { f, photos, urlText, addrQuery, step };

  const persistDraft = (oneShotToken = false) => {
    const marker = `${draftKey}:published`;
    if (sessionStorage.getItem(marker)) {
      // Publish succeeded — the draft was cleared; never resurrect it on unmount.
      if (oneShotToken) sessionStorage.removeItem(marker);
      return true;
    }
    const { access_instructions, ...safeF } = latestDraft.current.f;
    if (!draftHasContent(safeF, latestDraft.current.photos, latestDraft.current.urlText) && !localStorage.getItem(draftKey)) return false;
    localStorage.setItem(draftKey, JSON.stringify({ ...latestDraft.current, f: safeF, saved_at: new Date().toISOString() }));
    return false;
  };

  // Restore the latest draft once (same browser, same user) and resume at the
  // step the host stopped on. Restored values still go through normal validation.
  useEffect(() => {
    if (!draftKey || draftRestored.current) return;
    draftRestored.current = true;
    const d = loadListingDraft(draftKey);
    if (d && draftHasContent(d.f, d.photos, d.urlText)) {
      setF({ ...initial, ...d.f });
      if (typeof d.addrQuery === "string") setAddrQuery(d.addrQuery);
      if (Array.isArray(d.photos)) setPhotos(d.photos.filter((u) => typeof u === "string"));
      if (typeof d.urlText === "string") setUrlText(d.urlText);
      if (Number.isInteger(d.step) && d.step >= 0 && d.step <= 2) setStep(d.step);
      setDraftStatus("saved");
      toast.success("Draft restored — continue where you left off");
    }
    draftHydrated.current = true;
  }, [draftKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // Debounced autosave — writes after the host pauses typing, never per keystroke.
  useEffect(() => {
    if (!draftKey || !draftHydrated.current) return;
    if (!draftHasContent(f, photos, urlText)) { setDraftStatus(null); return; }
    setDraftStatus("saving");
    const t = setTimeout(() => {
      try { if (!persistDraft()) setDraftStatus("saved"); else setDraftStatus(null); }
      catch { setDraftStatus("error"); }
    }, 800);
    return () => clearTimeout(t);
  }, [draftKey, f, photos, urlText, addrQuery, step]); // eslint-disable-line react-hooks/exhaustive-deps

  // Flush on unmount (SPA navigation) AND beforeunload (refresh/tab close/hard
  // navigation — React cleanups don't run on document teardown) so text typed
  // inside the debounce window is not lost when leaving the page.
  useEffect(() => {
    if (!draftKey) return;
    const flush = () => {
      if (!draftHydrated.current || !latestDraft.current) return;
      try { persistDraft(true); } catch { /* storage unavailable */ }
    };
    window.addEventListener("beforeunload", flush);
    return () => { window.removeEventListener("beforeunload", flush); flush(); };
  }, [draftKey]); // eslint-disable-line react-hooks/exhaustive-deps
  // ---- End draft autosave ----

  const onPickPhotos = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    for (const file of files) {
      let tooMany = false;
      setPhotos((p) => { tooMany = p.length >= 8; return p; });
      if (tooMany) { toast.error("A listing can have at most 8 photos"); break; }
      if (!["image/png", "image/jpeg", "image/webp"].includes(file.type)) { toast.error(`${file.name}: must be PNG, JPEG or WebP`); continue; }
      if (file.size > 5 * 1024 * 1024) { toast.error(`${file.name}: must be under 5 MB`); continue; }
      setUploading((n) => n + 1);
      try {
        const r = await api.uploadListingPhoto(file);
        setPhotos((p) => [...p, r.url]);
      } catch (err) { toast.error(`${file.name}: ${err.message}`); }
      finally { setUploading((n) => n - 1); }
    }
  };

  // "Locate my address" — browser geolocation + the existing /api/geo/reverse
  // proxy (same infrastructure as Find Parking's "Near me"). Pins coords
  // immediately; fills the address text when reverse-geocode succeeds, and the
  // host can keep editing the text afterwards (stays in sync while pinned).
  const locate = () => {
    if (!navigator.geolocation) { toast.error("Geolocation is not supported by this browser — type the address instead"); return; }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const { latitude, longitude } = pos.coords;
        set("latitude", latitude); set("longitude", longitude);
        detectTimezone(latitude, longitude);
        try {
          const r = await api.geoReverse(latitude, longitude);
          if (r.formatted_address) { setAddrQuery(r.formatted_address); set("address", r.formatted_address); }
          else toast.info("Location pinned — confirm the street address below");
          // The physical location is authoritative — align the country to it
          // rather than saving mismatched country/coordinates (PRD §4).
          if (r.country_code) {
            const cc = String(r.country_code).toUpperCase();
            if (cc !== (f.country || initial?.country || "")) {
              set("country", cc);
              toast.info(`Country set to ${cc} based on your location`);
            }
          }
        } catch { toast.info("Location pinned — confirm the street address below"); }
        finally { setLocating(false); }
      },
      (err) => {
        setLocating(false);
        if (err.code === 1) toast.error("Location permission denied — type the address instead");
        else if (err.code === 3) toast.error("Location timed out — try again or type the address");
        else toast.error("Location unavailable — type the address instead");
      },
      { timeout: 10000, maximumAge: 60000 }
    );
  };

  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const toggle = (k, v) => setF((p) => ({ ...p, [k]: p[k].includes(v) ? p[k].filter((x) => x !== v) : [...p[k], v] }));

  const [tzFailed, setTzFailed] = useState(false);
  // The property's timezone is canonical — derived from the PINNED COORDINATES
  // via the server's offline lookup, never from the host's device. Manual
  // selection remains only as the fallback when detection genuinely fails.
  const detectTimezone = (lat, lng) => {
    if (lat == null || lng == null) return;
    setTzFailed(false);
    api.geoTimezone(lat, lng)
      .then((r) => { if (r.timezone) set("timezone", r.timezone); else setTzFailed(true); })
      .catch(() => setTzFailed(true));
  };

  const photoCount = photos.length + urlText.split("\n").map((u) => u.trim()).filter((u) => /^https?:\/\//.test(u)).length;
  const stepValid = () => {
    const authz = f.authorization_type || initial?.authorization_type || "";
    const locOk = !!(f.location_confirmed || initial?.location_confirmed);
    if (step === 0) return f.title.trim() && f.description.trim() && f.address.trim() && f.latitude != null && authz && locOk && !!(f.country || initial?.country);
    if (step === 1) return parseFloat(f.price_per_hour) > 0 && photoCount > 0;
    if (step === 2 && requirePolicy && !f.cancellation_policy) return false;
    return true;
  };

  // Property-authority declaration (server-versioned): required before any
  // publish; re-prompted on edit when the declaration version has changed.
  const [declDoc, setDeclDoc] = useState(null);
  const [declOpen, setDeclOpen] = useState(false);
  useEffect(() => { api.hostDeclaration().then(setDeclDoc).catch(() => {}); }, []);
  const declarationRequired = !initial?.attestation || !declDoc || initial.attestation.version !== declDoc.version;

  const doSubmit = async (withDeclaration) => {
    setBusy(true);
    try {
      await onSubmit({
        title: f.title.trim(), description: f.description.trim(), address: f.address.trim(),
        latitude: f.latitude, longitude: f.longitude,
        price_per_hour: parseFloat(f.price_per_hour),
        price_6h: f.price_6h ? parseFloat(f.price_6h) : null,
        price_12h: f.price_12h ? parseFloat(f.price_12h) : null,
        price_24h: f.price_24h ? parseFloat(f.price_24h) : null,
        monthly_enabled: !!f.monthly_enabled,
        monthly_price: f.monthly_enabled && f.monthly_price ? parseFloat(f.monthly_price) : null,
        space_type: f.space_type, amenities: f.amenities, booking_policy: f.booking_policy,
        cancellation_policy: f.cancellation_policy || null,
        currency: f.currency || "cad",
        timezone: f.timezone || null,
        country: f.country || null,
        region: f.region || null,
        postal_code: f.postal_code || null,
        flat_rate_enabled: !!f.flat_rate_enabled,
        flat_rate_duration_hours: f.flat_rate_enabled
          ? parseInt(f.flat_rate_duration_choice === "custom" ? f.flat_rate_custom_hours : f.flat_rate_duration_choice, 10) || null
          : null,
        flat_rate_price_cents: f.flat_rate_enabled && f.flat_rate_price ? Math.round(parseFloat(f.flat_rate_price) * 100) : null,
        max_duration_hours: f.booking_policy === "limited" && f.max_duration_hours ? parseInt(f.max_duration_hours, 10) : null,
        max_vehicle_height_m: f.max_vehicle_height_m ? parseFloat(f.max_vehicle_height_m) : null,
        vehicle_restrictions: f.vehicle_restrictions.trim() || null,
        access_instructions: f.access_instructions.trim() || null,
        images: [...photos, ...urlText.split("\n").map((u) => u.trim()).filter((u) => /^https?:\/\//.test(u))],
        available_days: f.available_days.length ? f.available_days : null,
        available_start_hour: f.available_start_hour !== "" ? parseInt(f.available_start_hour, 10) : null,
        available_end_hour: f.available_end_hour !== "" ? parseInt(f.available_end_hour, 10) : null,
        ...(f.authorization_type ? { authorization_type: f.authorization_type } : {}),
        ...(f.location_confirmed ? { location_confirmed: true } : {}),
        ...(withDeclaration ? { declaration_accepted: true } : {}),
      });
    } finally { setBusy(false); }
  };

  const submit = async () => {
    if (requirePolicy && !f.cancellation_policy) {
      toast.error("Please choose a cancellation policy first");
      return;
    }
    if (photoCount < 1) {
      toast.error("Photos required — add at least 1 photo of your space before publishing");
      return;
    }
    if (declarationRequired) { setDeclOpen(true); return; }
    await doSubmit(false);
  };

  const STEPS = ["Space info", "Pricing & photo", "Review"];

  return (
    <div className="card-dark p-6 sm:p-8" data-testid={`${testPrefix}-form`}>
      {draftKey && (
        <div className="h-5 mb-1 flex items-center justify-end" aria-live="polite" data-testid="listing-draft-status">
          {draftStatus === "saving" && <p className="text-[11px] text-zinc-500 flex items-center gap-1.5"><Loader2 className="w-3 h-3 animate-spin" /> Saving draft…</p>}
          {draftStatus === "saved" && <p className="text-[11px] text-zinc-500 flex items-center gap-1.5"><CheckCircle2 className="w-3 h-3 text-emerald-500" /> Draft saved</p>}
          {draftStatus === "error" && <p className="text-[11px] text-amber-400 flex items-center gap-1.5"><X className="w-3 h-3" /> Unable to save draft</p>}
        </div>
      )}
      <ol className="flex items-center gap-2 mb-8" aria-label="Progress">
        {STEPS.map((s, i) => (
          <li key={s} className="flex items-center gap-2 flex-1">
            <span className={`w-7 h-7 rounded-full text-xs font-bold flex items-center justify-center shrink-0 ${i <= step ? "bg-[#E50914] text-white" : "bg-[#1F2328] border border-[#27272A] text-zinc-500"}`}>{i + 1}</span>
            <span className={`text-xs font-semibold hidden sm:block ${i <= step ? "text-white" : "text-zinc-500"}`}>{s}</span>
            {i < STEPS.length - 1 && <span className="flex-1 h-px bg-[#27272A]" />}
          </li>
        ))}
      </ol>

      {step === 0 && (
        <div className="space-y-5" data-testid={`${testPrefix}-step-1`}>
          <div>
            <label htmlFor="lf-title" className="label-dark">Title</label>
            <input id="lf-title" data-testid={`${testPrefix}-title`} value={f.title} onChange={(e) => set("title", e.target.value)} maxLength={80}
                   className="input-dark" placeholder="e.g. Covered garage spot near Union Station" />
          </div>
          <div>
            <label className="label-dark">Country</label>
            <CountryPicker value={f.country || initial?.country || ""} testId={`${testPrefix}-country`}
                           onChange={(c) => {
                             // Country determines which addresses can be found —
                             // never keep the previous country's address/coords.
                             set("country", c); set("region", "");
                             set("address", ""); setAddrQuery("");
                             set("latitude", null); set("longitude", null);
                           }} />
          </div>
          <div>
            <label className="label-dark">Address (search, or locate me)</label>
            <div className="flex flex-col sm:flex-row gap-2.5">
              <div className="flex-1">
                <AddressAutocomplete value={addrQuery} country={f.country || initial?.country || null}
                  onChange={(v) => { setAddrQuery(v); if (f.latitude != null) set("address", v); }}
                  onSelect={(s) => {
                    setAddrQuery(s.description);
                    set("address", s.description);
                    if (s.lat != null && s.lng != null) { set("latitude", s.lat); set("longitude", s.lng); detectTimezone(s.lat, s.lng); }
                    if (s.country_code) set("country", String(s.country_code).toUpperCase());
                    if (s.region) set("region", s.region);
                    if (s.postal_code) set("postal_code", s.postal_code);
                  }}
                  testId={`${testPrefix}-address`} />
              </div>
              <button type="button" onClick={locate} disabled={locating} data-testid={`${testPrefix}-locate-btn`}
                      className="btn-secondary !px-4 !h-12 shrink-0" aria-label="Locate my address">
                {locating ? <Loader2 className="w-4 h-4 animate-spin" /> : <LocateFixed className="w-4 h-4" />} Locate my address
              </button>
            </div>
            {f.latitude != null && <p className="mt-2 text-xs text-emerald-400" data-testid={`${testPrefix}-coords-ok`}>Location pinned: {f.latitude.toFixed(5)}, {f.longitude.toFixed(5)}</p>}
          </div>

          {/* Trust & safety — why we verify (plain language, progressive disclosure) */}
          <div className="rounded-xl border border-[#27272A] bg-[#0D1114]/60 p-4 space-y-3.5" data-testid={`${testPrefix}-trust-block`}>
            <p className="text-xs text-zinc-500 leading-relaxed">We verify parking spaces to protect drivers and property owners from unauthorized listings.</p>
            <label className="flex items-start gap-2.5 cursor-pointer">
              <input type="checkbox" checked={!!(f.location_confirmed || initial?.location_confirmed)} onChange={(e) => set("location_confirmed", e.target.checked)}
                     data-testid={`${testPrefix}-location-confirm`} className="mt-0.5 accent-[#E50914]" />
              <span className="text-sm text-zinc-300">I confirm this is the actual location of the parking space</span>
            </label>
            <div>
              <label htmlFor={`${testPrefix}-authz`} className="label-dark">How are you authorized to offer this space?</label>
              <select id={`${testPrefix}-authz`} value={f.authorization_type || initial?.authorization_type || ""} onChange={(e) => set("authorization_type", e.target.value)}
                      data-testid={`${testPrefix}-authorization-type`} className="input-dark">
                <option value="">Select one…</option>
                <option value="own">I own this property</option>
                <option value="rent">I rent/lease this property</option>
                <option value="manage">I manage this property</option>
                <option value="permission">I have permission from the property owner</option>
                <option value="business">I represent a business/property manager</option>
              </select>
            </div>
            <p className="text-[11px] text-zinc-600 leading-relaxed" data-testid={`${testPrefix}-declaration-note`}>
              Before publishing you will review and accept the <span className="text-zinc-300 font-medium">Property Authority &amp; Listing Responsibility</span> declaration.
            </p>
            <p className="text-[0.7rem] text-zinc-600">Your first listing is reviewed by our team before it can accept bookings.</p>
          </div>
          <div>
            <label className="label-dark">Space type</label>
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-2" role="radiogroup" aria-label="Space type">
              {SPACE_TYPES.map((t) => (
                <button key={t} type="button" role="radio" aria-checked={f.space_type === t} data-testid={`${testPrefix}-type-${t.toLowerCase()}`} onClick={() => set("space_type", t)}
                        className={`px-3 py-2.5 rounded-xl text-xs font-semibold border transition-colors ${f.space_type === t ? "bg-[#E50914] border-[#E50914]" : "bg-[#080B0D] border-[#27272A] text-zinc-300 hover:border-[#3F3F46]"}`}>{t}</button>
              ))}
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label htmlFor="lf-height" className="label-dark">Max vehicle height (m) <span className="normal-case text-zinc-600">(optional)</span></label>
              <input id="lf-height" data-testid={`${testPrefix}-max-height`} type="number" min="0.5" max="6" step="0.1" value={f.max_vehicle_height_m} onChange={(e) => set("max_vehicle_height_m", e.target.value)} className="input-dark" placeholder="e.g. 2.0" />
            </div>
            <div>
              <label htmlFor="lf-veh" className="label-dark">Vehicle restrictions <span className="normal-case text-zinc-600">(optional)</span></label>
              <input id="lf-veh" data-testid={`${testPrefix}-vehicle-restrictions`} maxLength={200} value={f.vehicle_restrictions} onChange={(e) => set("vehicle_restrictions", e.target.value)} className="input-dark" placeholder="e.g. No oversized trucks" />
            </div>
          </div>
          <div>
            <label htmlFor="lf-desc" className="label-dark">Description</label>
            <textarea id="lf-desc" data-testid={`${testPrefix}-description`} rows={4} value={f.description} onChange={(e) => set("description", e.target.value)}
                      className="input-dark !h-auto py-3" placeholder="Size limits, entry details, nearby landmarks…" />
          </div>
        </div>
      )}

      {step === 1 && (
        <div className="space-y-5" data-testid={`${testPrefix}-step-2`}>
          <div>
            <label htmlFor="lf-price" className="label-dark">Price per hour ({(f.currency || "cad").toUpperCase()})</label>
            <input id="lf-price" data-testid={`${testPrefix}-price`} type="number" min="0.5" step="0.5" value={f.price_per_hour} onChange={(e) => set("price_per_hour", e.target.value)} className="input-dark" placeholder="8.00" />
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label htmlFor="lf-currency" className="label-dark">Currency</label>
              <select id="lf-currency" data-testid={`${testPrefix}-currency`} value={f.currency} onChange={(e) => set("currency", e.target.value)} className="input-dark">
                <option value="cad">CAD — Canadian dollar</option>
                <option value="usd">USD — US dollar</option>
              </select>
              <p className="mt-1.5 text-[11px] text-zinc-600">All your listings share one currency — no currency conversion at checkout.</p>
            </div>
            <div>
              <label htmlFor="lf-timezone" className="label-dark">Listing timezone</label>
              {f.timezone && f.latitude != null && !tzFailed ? (
                <div className="input-dark flex items-center" data-testid={`${testPrefix}-timezone-auto`}>
                  <span className="text-sm text-white">All times are in the property's local timezone — <span className="font-semibold">{tzLabel(f.timezone)}</span></span>
                </div>
              ) : (
                <select id="lf-timezone" data-testid={`${testPrefix}-timezone`} value={f.timezone} onChange={(e) => set("timezone", e.target.value)} className="input-dark">
                  <option value="">Select timezone…</option>
                  {[...new Set([f.timezone, ...TZ_OPTIONS])].filter(Boolean).map((tz) => (
                    <option key={tz} value={tz}>{tz.replace(/_/g, " ")}</option>
                  ))}
                </select>
              )}
              <p className="mt-1.5 text-[11px] text-zinc-600" data-testid={`${testPrefix}-timezone-hint`}>
                {f.latitude != null && !tzFailed
                  ? "Detected automatically from the pinned property location. Availability days/hours are interpreted in this timezone."
                  : "Pin the property location above to auto-detect its timezone — or choose it manually. Availability days/hours are interpreted in this timezone."}
              </p>
            </div>
          </div>
          <div className="grid sm:grid-cols-3 gap-4">
            {[["price_6h", "6-hour price"], ["price_12h", "12-hour price"], ["price_24h", "24-hour price"]].map(([k, l]) => (
              <div key={k}>
                <label htmlFor={`lf-${k}`} className="label-dark">{l} <span className="normal-case text-zinc-600">(optional)</span></label>
                <input id={`lf-${k}`} data-testid={`${testPrefix}-${k}`} type="number" min="0" step="0.5" value={f[k]} onChange={(e) => set(k, e.target.value)} className="input-dark" placeholder="—" />
              </div>
            ))}
          </div>
          <div className="p-5 rounded-xl border border-[#27272A] bg-[#0D1114]/60" data-testid={`${testPrefix}-flat-rate`}>
            <div className="flex items-center gap-3">
              <input id="lf-flat-enabled" data-testid={`${testPrefix}-flat-enabled`} type="checkbox" checked={!!f.flat_rate_enabled} onChange={(e) => set("flat_rate_enabled", e.target.checked)} className="w-4 h-4 accent-[#E50914]" />
              <label htmlFor="lf-flat-enabled" className="text-sm text-zinc-300">Offer a flat rate <span className="text-zinc-600">— one fixed price for a parked window (e.g. overnight)</span></label>
            </div>
            {f.flat_rate_enabled && (
              <div className="mt-4 grid sm:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="lf-flat-duration" className="label-dark">Flat-rate duration</label>
                  <select id="lf-flat-duration" data-testid={`${testPrefix}-flat-duration`} value={f.flat_rate_duration_choice || "12"} onChange={(e) => set("flat_rate_duration_choice", e.target.value)} className="input-dark">
                    <option value="12">12 hours</option>
                    <option value="24">24 hours</option>
                    <option value="custom">Custom…</option>
                  </select>
                </div>
                {f.flat_rate_duration_choice === "custom" && (
                  <div>
                    <label htmlFor="lf-flat-custom-hours" className="label-dark">Custom duration (1–168 hours)</label>
                    <input id="lf-flat-custom-hours" data-testid={`${testPrefix}-flat-custom-hours`} type="number" min="1" max="168" step="1" value={f.flat_rate_custom_hours} onChange={(e) => set("flat_rate_custom_hours", e.target.value)} className="input-dark" placeholder="18" />
                  </div>
                )}
                <div>
                  <label htmlFor="lf-flat-price" className="label-dark">Flat price ({(f.currency || "cad").toUpperCase()})</label>
                  <input id="lf-flat-price" data-testid={`${testPrefix}-flat-price`} type="number" min="0.5" step="0.5" value={f.flat_rate_price} onChange={(e) => set("flat_rate_price", e.target.value)} className="input-dark" placeholder="25.00" />
                  <p className="mt-1.5 text-[11px] text-zinc-600">One fixed price for the whole window — drivers pay it plus the MyRedPark service fee shown at checkout.</p>
                </div>
              </div>
            )}
          </div>
          <div className="flex items-center gap-3">
            <input id="lf-monthly" data-testid={`${testPrefix}-monthly-enabled`} type="checkbox" checked={f.monthly_enabled} onChange={(e) => set("monthly_enabled", e.target.checked)} className="w-4 h-4 accent-[#E50914]" />
            <label htmlFor="lf-monthly" className="text-sm text-zinc-300">Offer monthly parking</label>
            {f.monthly_enabled && (
              <input data-testid={`${testPrefix}-monthly-price`} type="number" min="1" step="1" value={f.monthly_price} onChange={(e) => set("monthly_price", e.target.value)} className="input-dark !w-36" placeholder="$ / month" aria-label="Monthly price" />
            )}
          </div>
          <div>
            <label className="label-dark">Amenities</label>
            <div className="flex flex-wrap gap-2">
              {AMENITY_SUGGESTIONS.map((a) => (
                <button key={a} type="button" data-testid={`${testPrefix}-amenity-${a.toLowerCase().replace(/\s+/g, "-")}`} onClick={() => toggle("amenities", a)}
                        className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${f.amenities.includes(a) ? "bg-[#E50914]/15 border-[#E50914] text-[#FF3B45]" : "bg-[#080B0D] border-[#27272A] text-zinc-400 hover:border-[#3F3F46]"}`}>{a}</button>
              ))}
            </div>
          </div>
          <div>
            <label className="label-dark">Photos <span className="text-[#FF3B45] normal-case">required</span> <span className="normal-case text-zinc-600">(at least 1 — up to 8; PNG, JPEG or WebP, max 5 MB each)</span></label>
            {photos.length > 0 && (
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-3" data-testid={`${testPrefix}-photo-grid`}>
                {photos.map((u, i) => (
                  <div key={u} className="relative rounded-xl overflow-hidden border border-[#27272A] aspect-[4/3]">
                    <img src={u} alt={`Listing photo ${i + 1}`} className="w-full h-full object-cover" data-testid={`${testPrefix}-photo-${i}`} />
                    <button type="button" onClick={() => setPhotos((p) => p.filter((x) => x !== u))} aria-label={`Remove photo ${i + 1}`}
                            data-testid={`${testPrefix}-photo-remove-${i}`}
                            className="absolute top-1.5 right-1.5 w-6 h-6 rounded-full bg-black/70 text-zinc-300 hover:text-white flex items-center justify-center">
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            )}
            <label data-testid={`${testPrefix}-photo-upload`}
                   className={`btn-secondary !px-4 !py-2.5 text-xs inline-flex items-center gap-2 ${uploading ? "opacity-60 cursor-wait" : "cursor-pointer"}`}>
              {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ImagePlus className="w-3.5 h-3.5" />}
              {uploading ? "Uploading…" : "Upload photos"}
              <input type="file" accept="image/png,image/jpeg,image/webp" multiple className="hidden" onChange={onPickPhotos}
                     disabled={uploading > 0} data-testid={`${testPrefix}-photo-input`} />
            </label>
            <p className="mt-1.5 text-[11px] text-zinc-600">Photos help drivers recognize the space on arrival. They are publicly visible on the listing; they are not proof of ownership or authorization.</p>
            <textarea id="lf-images" data-testid={`${testPrefix}-images`} rows={2} value={urlText} onChange={(e) => setUrlText(e.target.value)} className="input-dark !h-auto py-3 font-mono text-xs mt-3" placeholder="Or paste https:// image URLs, one per line" />
          </div>
          <div>
            <label htmlFor="lf-access" className="label-dark">Access instructions <span className="normal-case text-zinc-600">(revealed only after booking is confirmed)</span></label>
            <textarea id="lf-access" data-testid={`${testPrefix}-access`} rows={2} value={f.access_instructions} onChange={(e) => set("access_instructions", e.target.value)} className="input-dark !h-auto py-3" placeholder="Gate code, entry instructions, parking level…" />
          </div>
        </div>
      )}

      {step === 2 && (
        <div className="space-y-6" data-testid={`${testPrefix}-step-3`}>
          <div>
            <label className="label-dark">Booking policy</label>
            <div className="flex gap-2">
              {[["open", "Open — any duration"], ["limited", "Limited — max duration"]].map(([v, l]) => (
                <button key={v} type="button" data-testid={`${testPrefix}-policy-${v}`} onClick={() => set("booking_policy", v)}
                        className={`px-4 py-2.5 rounded-xl text-xs font-semibold border transition-colors ${f.booking_policy === v ? "bg-[#E50914] border-[#E50914]" : "bg-[#080B0D] border-[#27272A] text-zinc-300"}`}>{l}</button>
              ))}
            </div>
            {f.booking_policy === "limited" && (
              <input data-testid={`${testPrefix}-max-duration`} type="number" min="1" value={f.max_duration_hours} onChange={(e) => set("max_duration_hours", e.target.value)} className="input-dark mt-3 !w-48" placeholder="Max hours" aria-label="Max duration hours" />
            )}
          </div>
          <div>
            <label className="label-dark">Cancellation policy {requirePolicy ? "" : <span className="normal-case text-zinc-600">(optional)</span>}</label>
            <div className="grid sm:grid-cols-3 gap-2" role="radiogroup" aria-label="Cancellation policy">
              {CANCELLATION_POLICIES.map((p) => (
                <button key={p.value} type="button" role="radio" aria-checked={f.cancellation_policy === p.value}
                        data-testid={`${testPrefix}-cancel-policy-${p.value}`} onClick={() => set("cancellation_policy", p.value)}
                        className={`p-3.5 rounded-xl border text-left transition-colors ${f.cancellation_policy === p.value ? "bg-[#E50914]/10 border-[#E50914]" : "bg-[#080B0D] border-[#27272A] hover:border-[#3F3F46]"}`}>
                  <span className={`block text-xs font-bold ${f.cancellation_policy === p.value ? "text-[#FF3B45]" : "text-zinc-200"}`}>{p.label}</span>
                  <span className="block mt-1 text-[11px] leading-snug text-zinc-500">{p.hostHint}</span>
                </button>
              ))}
            </div>
            <p className="mt-2 text-[11px] text-zinc-600 leading-relaxed">
              Shown to drivers before they book. Cancellation is always free before payment authorization; after capture, changes go through support review.
            </p>
          </div>
          <div>
            <label className="label-dark">Available days <span className="normal-case text-zinc-600">(none selected = every day)</span></label>
            <div className="flex flex-wrap gap-2">
              {DAYS.map((d, i) => (
                <button key={d} type="button" data-testid={`${testPrefix}-day-${d.toLowerCase()}`} onClick={() => toggle("available_days", i)}
                        className={`px-3.5 py-2 rounded-xl text-xs font-semibold border transition-colors ${f.available_days.includes(i) ? "bg-[#E50914] border-[#E50914]" : "bg-[#080B0D] border-[#27272A] text-zinc-300"}`}>{d}</button>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label htmlFor="lf-sh" className="label-dark">Available from hour <span className="normal-case text-zinc-600">(optional)</span></label>
              <input id="lf-sh" data-testid={`${testPrefix}-start-hour`} type="number" min="0" max="23" value={f.available_start_hour} onChange={(e) => set("available_start_hour", e.target.value)} className="input-dark" placeholder="0–23" />
            </div>
            <div>
              <label htmlFor="lf-eh" className="label-dark">Available until hour</label>
              <input id="lf-eh" data-testid={`${testPrefix}-end-hour`} type="number" min="0" max="24" value={f.available_end_hour} onChange={(e) => set("available_end_hour", e.target.value)} className="input-dark" placeholder="0–24" />
            </div>
          </div>
          <div className="p-4 rounded-xl bg-[#080B0D] border border-[#27272A] text-sm space-y-1.5" data-testid={`${testPrefix}-review-summary`}>
            <p className="font-bold">{f.title || "Untitled"}</p>
            <p className="text-zinc-400 text-xs">{f.address}</p>
            <p className="text-zinc-400 text-xs">{f.space_type} · {fmtMoney(parseFloat(f.price_per_hour) || 0)}/hr · {f.amenities.length} amenities</p>
            {policyLabel(f.cancellation_policy) && <p className="text-zinc-400 text-xs">Cancellation policy: <span className="text-zinc-200 font-medium">{policyLabel(f.cancellation_policy)}</span></p>}
          </div>
          <div className="p-5 rounded-xl border border-[#27272A] bg-[#080B0D] flex flex-col sm:flex-row gap-5 items-start" data-testid={`${testPrefix}-signage`}>
            <img src="/myredpark-parking-sign.jpg" alt="MyRedPark dedicated parking sign preview" className="w-24 rounded-lg border border-[#27272A] shrink-0" data-testid={`${testPrefix}-signage-preview`} />
            <div className="min-w-0">
              <p className="meta-label !text-[#FF3B45]">Recommended for hosts</p>
              <p className="mt-2 text-sm text-zinc-300 leading-relaxed">Help drivers identify the correct parking space when they arrive. We recommend displaying a dedicated MyRedPark parking sign at your approved parking space.</p>
              <a href="/myredpark-parking-sign.jpg" download="myredpark-parking-sign.jpg" data-testid={`${testPrefix}-signage-download`}
                 className="btn-secondary !px-4 !py-2 text-xs mt-3 inline-flex items-center gap-1.5">
                <Download className="w-3.5 h-3.5" /> Download MyRedPark Parking Sign
              </a>
              <p className="mt-2.5 text-[11px] text-zinc-600 leading-relaxed">The sign is optional and does not replace MyRedPark listing verification or authorization requirements.</p>
            </div>
          </div>
          {/* Publish gate — payout readiness is DISPLAY-ONLY here; the server
              re-verifies live with Stripe on every publish request. */}
          {payoutGate && (
            <div className="p-5 rounded-xl border border-[#27272A] bg-[#080B0D]" data-testid={`${testPrefix}-payout-gate`}>
              <p className="meta-label !text-[#FF3B45]">Receive your parking earnings</p>
              {payoutGate.loading ? (
                <div className="mt-3 h-4 w-48 rounded bg-[#1F2328] animate-pulse" data-testid={`${testPrefix}-payout-loading`} />
              ) : payoutGate.state === "ready" ? (
                <p className="mt-2.5 flex items-center gap-2 text-sm font-semibold text-emerald-400" data-testid={`${testPrefix}-payout-ready`}>
                  <CheckCircle2 className="w-4 h-4" /> Payout account connected
                </p>
              ) : (
                <div className="mt-2.5 space-y-3" data-testid={`${testPrefix}-payout-required`}>
                  <p className="text-sm text-zinc-300">Connect your payout account before publishing this listing.</p>
                  <p className="text-xs font-semibold text-amber-400">Payout account: Setup required</p>
                  <button type="button" onClick={payoutGate.onSetup} data-testid={`${testPrefix}-setup-payouts`}
                          className="btn-secondary !px-4 !py-2 text-xs inline-flex items-center gap-1.5">
                    Set Up Payouts <ChevronRight className="w-3.5 h-3.5" />
                  </button>
                  <p className="text-[11px] text-zinc-600 leading-relaxed">Bank details are collected securely by Stripe — MyRedPark never sees or stores them.</p>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      <div className="mt-8 flex justify-between">
        <button type="button" onClick={() => setStep((s) => s - 1)} disabled={step === 0} data-testid={`${testPrefix}-back`} className="btn-secondary !px-5 text-sm">
          <ChevronLeft className="w-4 h-4" /> Back
        </button>
        {step < 2 ? (
          <button type="button" onClick={() => stepValid() ? setStep((s) => s + 1) : toast.error("Complete the required fields first")} disabled={!stepValid()} data-testid={`${testPrefix}-next`} className="btn-primary !px-5 text-sm disabled:opacity-40 disabled:cursor-not-allowed">
            Next <ChevronRight className="w-4 h-4" />
          </button>
        ) : (
          <button type="button" onClick={submit} disabled={busy || (payoutGate ? payoutGate.state !== "ready" : false)} data-testid={`${testPrefix}-submit`} className="btn-primary !px-6 text-sm">
            {busy && <Loader2 className="w-4 h-4 animate-spin" />} {submitLabel}
          </button>
        )}
      </div>

      {/* Property Authority & Listing Responsibility declaration — appears after
          the ownership/authorization details and before publication. Declining
          closes the flow and preserves every field (nothing is submitted). */}
      {declOpen && (
        <HostDeclaration
          relationship={f.authorization_type || initial?.authorization_type || ""}
          onCancel={() => setDeclOpen(false)}
          onAccept={async () => { setDeclOpen(false); await doSubmit(true); }}
        />
      )}
    </div>
  );
}

export default function HostNewListing() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const draftKey = user?.user_id ? listingDraftKey(user.user_id) : null;
  const [searchParams] = useSearchParams();
  const [payout, setPayout] = useState(null);
  const [payoutLoading, setPayoutLoading] = useState(true);
  useEffect(() => setPageTitle("Create Listing"), []);

  // Live Stripe-derived payout status — refreshed on mount and after returning
  // from Stripe-hosted onboarding (?return=1 via the onboarding return_path).
  const refreshPayout = () => {
    setPayoutLoading(true);
    api.connectStatus().then(setPayout).catch(() => setPayout(null)).finally(() => setPayoutLoading(false));
  };
  useEffect(refreshPayout, []);
  useEffect(() => {
    if (searchParams.get("return")) {
      toast.success("Stripe onboarding updated — status refreshed");
      refreshPayout();
    }
  }, [searchParams]); // eslint-disable-line react-hooks/exhaustive-deps

  const setupPayouts = async (country) => {
    try {
      const r = await api.connectOnboarding(country, "/host/listings/new");
      window.location.href = r.url; // Stripe-hosted onboarding — bank details never touch MyRedPark.
    } catch (e) { toast.error(e.message); }
  };

  return (
    <div className="min-h-screen flex flex-col" data-testid="create-listing-page">
      <Navbar />
      <main className="flex-1 max-w-3xl w-full mx-auto px-5 sm:px-8 pt-28 pb-16">
        <p className="section-label">Host</p>
        <h1 className="type-display mt-3">List your parking space</h1>
        <p className="mt-1 text-sm text-zinc-400">Three quick steps. You can edit or pause the listing any time.</p>
        <div className="mt-8">
          {!draftKey ? <PageLoading label="Loading…" /> : (
          <ListingForm initial={emptyListing} submitLabel="Publish Listing" testPrefix="listing" requirePolicy draftKey={draftKey}
            payoutGate={{
              state: payout?.state || null,
              loading: payoutLoading,
              onSetup: () => setupPayouts(undefined),
            }}
            onSubmit={async (body) => {
              try {
                const s = await api.createSpot(body);
                // Published — drop the draft and arm the one-shot marker so the
                // unmount flush cannot resurrect it.
                sessionStorage.setItem(`${draftKey}:published`, "1");
                clearListingDraft(draftKey);
                if (s.status === "active") { toast.success("Listing published"); navigate(`/spot/${s.spot_id}`); }
                else { toast.success("Listing submitted — our team will review it before it can accept bookings."); navigate("/host"); }
              } catch (e) {
                if (e.message === "IDENTITY_VERIFICATION_REQUIRED") {
                  toast.error("Verify your identity before publishing", {
                    action: { label: "Verify now", onClick: () => navigate("/profile") },
                  });
                } else toast.error(e.message);
              }
            }} />
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
